import React, { useMemo, useState } from 'react';
import { Student } from '../types';
import ApiService from '../services/api';
import './StudentTransferButton.css';
import { useClassroomData, processedClassroomData } from '../data/classroomData';

interface StudentTransferButtonProps {
  students: Student[];
  currentClassroomId: string;
}

const StudentTransferButton: React.FC<StudentTransferButtonProps> = ({ 
  students,
  currentClassroomId 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const classroomData = useClassroomData().data;
  const { classroomOptions } = useMemo(()=> processedClassroomData(classroomData), [classroomData]);
  const refreshData = useClassroomData().refetch;

  // Determine if we're transferring a single student or multiple
  const studentCount = students.length;
  const isBulkTransfer = studentCount > 1;
  const transferStudentName = isBulkTransfer ? `${studentCount} students` : students[0]?.name || 'student';

  // Filter out the current classroom from options
  const availableClassrooms = classroomOptions.filter(
    classroom => classroom.id !== currentClassroomId && classroom.section != 'Other'
  );

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
    setError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
  };

  const handleTransfer = async (targetClassroomId: string) => {
    // Collect all student IDs to transfer
    const childrenIds = students.filter(s => s.id).map(s => s.id as string);
    
    if (childrenIds.length === 0) {
      setError('No valid student IDs found for transfer');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await ApiService.moveStudentsToClass({
        childrenIds,
        targetClassId: targetClassroomId
      });
      
      // Close modal and refresh classroom data
      setIsOpen(false);
      await refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transfer student(s)');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render anything if there are no students to transfer
  if (students.length === 0) {
    return null;
  }

  return (
    <span className="student-transfer-button-component">
      <span 
        className="student-transfer-icon" 
        title={`Transfer ${isBulkTransfer ? 'all students' : 'to another classroom'}`}
        onClick={handleOpen}
      />
      
      {isOpen && (
        <div className="modal-overlay" onClick={handleClose}>
          <div className="transfer-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Transfer {transferStudentName}</h3>
              <button className="close-button" onClick={handleClose}>×</button>
            </div>
            
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              
              <p>Select a classroom to transfer {isBulkTransfer ? 'all students' : 'to'}:</p>
              
              {availableClassrooms.length > 0 ? (
                <div className="classroom-list">
                  {availableClassrooms.map(classroom => (
                    <button
                      key={classroom.id}
                      className="classroom-option"
                      onClick={() => handleTransfer(classroom.id)}
                      disabled={isLoading}
                    >
                      {classroom.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="no-options">No other classrooms available</p>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default StudentTransferButton;